export const PROFILE_EDITOR_PREVIEW_SIZE = 220;
export const PROFILE_EDITOR_MIN_ZOOM = 1;
export const PROFILE_EDITOR_MAX_ZOOM = 2.5;
export const PROFILE_IMAGE_OUTPUT_SIZE = 600;
export const PROFILE_IMAGE_OUTPUT_EXTENSION = "jpg";
export const PROFILE_IMAGE_OUTPUT_TYPE = "image/jpeg";
export const PROFILE_IMAGE_OUTPUT_QUALITY = 0.92;
export const PROFILE_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
export const PROFILE_IMAGE_BACKGROUND = "#ffffff";

// Validates a selected profile image before the editor tries to load or upload it.
export function validateProfileImageFile(file) {
  if (!file) return "Please select an image file";
  if (!String(file.type || "").startsWith("image/")) {
    return "Please select a valid image file";
  }
  if (file.size > PROFILE_IMAGE_MAX_BYTES) {
    return "Image size must be less than 5MB";
  }
  return "";
}

// Computes the rendered preview size for the profile image editor at the current zoom level.
export function computeRenderedImageSize(dimensions, zoom = PROFILE_EDITOR_MIN_ZOOM) {
  if (!dimensions?.width || !dimensions?.height) {
    const size = PROFILE_EDITOR_PREVIEW_SIZE * zoom;
    return { width: size, height: size };
  }

  const { width, height } = dimensions;
  const baseScale = Math.max(
    PROFILE_EDITOR_PREVIEW_SIZE / width,
    PROFILE_EDITOR_PREVIEW_SIZE / height
  );

  return {
    width: width * baseScale * zoom,
    height: height * baseScale * zoom,
  };
}

// Returns the maximum draggable offsets allowed for the current preview image dimensions and zoom.
export function computeImageOffsetLimits(dimensions, zoom = PROFILE_EDITOR_MIN_ZOOM) {
  const rendered = computeRenderedImageSize(dimensions, zoom);
  return {
    x: Math.max(0, (rendered.width - PROFILE_EDITOR_PREVIEW_SIZE) / 2),
    y: Math.max(0, (rendered.height - PROFILE_EDITOR_PREVIEW_SIZE) / 2),
  };
}

// Clamps drag offsets so the cropped preview never exposes empty space in the editor frame.
export function clampImageOffsets(dimensions, offsetX, offsetY, zoom = PROFILE_EDITOR_MIN_ZOOM) {
  const limits = computeImageOffsetLimits(dimensions, zoom);
  const x = limits.x <= 0 ? 0 : Math.max(-limits.x, Math.min(limits.x, offsetX));
  const y = limits.y <= 0 ? 0 : Math.max(-limits.y, Math.min(limits.y, offsetY));
  return { x, y };
}

// Safely releases a temporary object URL after preview or image-loading work completes.
export function revokeObjectUrl(value) {
  if (!value) return;
  try {
    URL.revokeObjectURL(value);
  } catch (_) {
    // no-op
  }
}

// Loads natural image dimensions from an object URL before the editor initializes crop state.
export async function loadImageDimensionsFromUrl(objectUrl) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      resolve({
        width: image.naturalWidth || image.width,
        height: image.naturalHeight || image.height,
      });
    };
    image.onerror = () => reject(new Error("Failed to load selected image"));
    image.src = objectUrl;
  });
}

async function loadImageElement(file) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);
    image.onload = () => {
      revokeObjectUrl(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      revokeObjectUrl(objectUrl);
      reject(new Error("Failed to load selected image"));
    };
    image.src = objectUrl;
  });
}

// Renders the cropped profile image output blob used by onboarding and edit-profile uploads.
export async function renderCroppedProfileImage({
  file,
  zoom,
  offsetX,
  offsetY,
}) {
  const source = await loadImageElement(file);
  const canvas = document.createElement("canvas");
  canvas.width = PROFILE_IMAGE_OUTPUT_SIZE;
  canvas.height = PROFILE_IMAGE_OUTPUT_SIZE;

  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Unable to prepare profile image");
  }

  context.fillStyle = PROFILE_IMAGE_BACKGROUND;
  context.fillRect(0, 0, PROFILE_IMAGE_OUTPUT_SIZE, PROFILE_IMAGE_OUTPUT_SIZE);

  const baseScale = Math.max(
    PROFILE_IMAGE_OUTPUT_SIZE / source.width,
    PROFILE_IMAGE_OUTPUT_SIZE / source.height
  );
  const finalScale = baseScale * zoom;
  const drawWidth = source.width * finalScale;
  const drawHeight = source.height * finalScale;

  const offsetScale = PROFILE_IMAGE_OUTPUT_SIZE / PROFILE_EDITOR_PREVIEW_SIZE;
  const scaledOffsetX = offsetX * offsetScale;
  const scaledOffsetY = offsetY * offsetScale;
  const maxOffsetX = Math.max(0, (drawWidth - PROFILE_IMAGE_OUTPUT_SIZE) / 2);
  const maxOffsetY = Math.max(0, (drawHeight - PROFILE_IMAGE_OUTPUT_SIZE) / 2);
  const safeOffsetX = Math.max(-maxOffsetX, Math.min(maxOffsetX, scaledOffsetX));
  const safeOffsetY = Math.max(-maxOffsetY, Math.min(maxOffsetY, scaledOffsetY));
  const drawX = (PROFILE_IMAGE_OUTPUT_SIZE - drawWidth) / 2 + safeOffsetX;
  const drawY = (PROFILE_IMAGE_OUTPUT_SIZE - drawHeight) / 2 + safeOffsetY;

  context.drawImage(source, drawX, drawY, drawWidth, drawHeight);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Failed to process profile image"));
          return;
        }
        resolve(blob);
      },
      PROFILE_IMAGE_OUTPUT_TYPE,
      PROFILE_IMAGE_OUTPUT_QUALITY
    );
  });
}
